// Brand page builder (X-11): live selection, tier ladder, totals and one-request add to cart.
// Prices come from the rendered variants (live Shopify prices); tiers come from data-tiers. Nothing is hard-coded here.
// "units:percent, …" -> [{ units, percent }] in ascending units.
const parseTiers = text => (text || '').split(',')
  .map(pair => pair.split(':').map(Number))
  .filter(([units, percent]) => Number.isFinite(units) && units > 0 && Number.isFinite(percent) && percent >= 0)
  .map(([units, percent]) => ({ units, percent }))
  .sort((a, b) => a.units - b.units);

if (!customElements.get('brand-builder')) {
  customElements.define('brand-builder', class BrandBuilder extends HTMLElement {
    connectedCallback() {
      this.tiers = parseTiers(this.dataset.tiers);
      this.rows = [...this.querySelectorAll('[data-row]')];
      // Each product's own quantity ladder (data-product-tiers, from the discount codes that exist).
      this.productTiers = new Map(this.rows.map(row => [row.dataset.productId, parseTiers(row.dataset.productTiers)]));
      this.format = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.addButton = this.querySelector('[data-add]');
      this.renderLadder();
      this.addEventListener('change', event => { if (event.target.matches('[data-row-quantity]')) this.update(); });
      this.addEventListener('input', event => { if (event.target.matches('[data-row-quantity]')) this.update(); });
      this.addButton?.addEventListener('click', () => this.addToCart());
      this.update();
    }

    money(cents) { return this.format.format(cents / 100); }
    percentText(percent) { return `${percent.toLocaleString(this.dataset.locale || undefined, { maximumFractionDigits: 2 })}%`; }
    unitsText(count) { return `${count} ${count === 1 ? this.dataset.unitsOne : this.dataset.unitsMany}`; }

    renderLadder() {
      const ladder = this.querySelector('[data-ladder]');
      if (!ladder) return;
      ladder.innerHTML = '';
      this.tiers.forEach((tier, index) => {
        const last = index === this.tiers.length - 1;
        const item = document.createElement('li');
        item.className = 'brand-builder__tier';
        const units = document.createElement('span');
        // Ranges, as in the client's brand-savings table: "1 – 3 units", "4 – 9 units", …, "21+ units".
        const upTo = last ? 0 : this.tiers[index + 1].units - 1;
        if (last && index > 0) units.textContent = `${tier.units}+ ${this.dataset.unitsMany}`;
        else if (upTo > tier.units) units.textContent = `${tier.units} – ${upTo} ${this.dataset.unitsMany}`;
        else units.textContent = this.unitsText(tier.units);
        const percent = document.createElement('strong');
        percent.textContent = last && index > 0 && this.dataset.maxLabel ? `${this.percentText(tier.percent)} ${this.dataset.maxLabel}` : this.percentText(tier.percent);
        item.append(units, percent);
        ladder.append(item);
      });
      this.tierItems = [...ladder.children];
    }

    quantity(row) {
      const input = row.querySelector('[data-row-quantity]');
      if (!input || input.disabled) return 0;
      const value = Math.floor(Number(input.value));
      if (!Number.isFinite(value) || value < 0) { input.value = 0; return 0; }
      return value;
    }

    update() {
      const selected = this.rows.map(row => ({ row, quantity: this.quantity(row) })).filter(line => line.quantity > 0);
      const units = selected.reduce((sum, line) => sum + line.quantity, 0);
      const subtotal = selected.reduce((sum, line) => sum + Number(line.row.dataset.price) * line.quantity, 0);
      let activeIndex = -1;
      this.tiers.forEach((tier, index) => { if (units >= tier.units) activeIndex = index; });
      const percent = activeIndex >= 0 ? this.tiers[activeIndex].percent : 0;
      // Same rounding as the brand-deal discount code at checkout: Shopify takes the percentage off each unit and rounds
      // each unit's saving down to the cent (Nabota 200U €78.92 at 5% saves €3.94, not €3.95).
      // A line can also reach its own product's volume rung; Shopify then gives it whichever saving is larger.
      const productUnits = new Map();
      selected.forEach(line => productUnits.set(line.row.dataset.productId, (productUnits.get(line.row.dataset.productId) || 0) + line.quantity));
      const ownPercent = line => (this.productTiers.get(line.row.dataset.productId) || [])
        .filter(tier => productUnits.get(line.row.dataset.productId) >= tier.units)
        .reduce((best, tier) => Math.max(best, tier.percent), 0);
      const mixed = selected.some(line => ownPercent(line) > percent);
      const discount = selected.reduce((sum, line) => sum + Math.floor(Number(line.row.dataset.price) * Math.max(percent, ownPercent(line)) / 100) * line.quantity, 0);

      this.renderLines(selected);
      this.tierItems?.forEach((item, index) => {
        item.classList.toggle('is-active', index === activeIndex);
        item.toggleAttribute('aria-current', index === activeIndex);
      });

      const next = this.tiers.find(tier => tier.units > units && tier.percent > percent);
      const nudge = this.querySelector('[data-nudge-text]');
      if (nudge) {
        const top = this.tiers.at(-1);
        if (units === 0) nudge.textContent = this.dataset.nudgeEmpty || '';
        else if (next) {
          const missing = next.units - units;
          nudge.textContent = (this.dataset.nudge || '')
            .replace('[count]', missing)
            .replace('[units]', missing === 1 ? this.dataset.unitsOne : this.dataset.unitsMany)
            .replace('[percent]', this.percentText(next.percent));
        } else nudge.textContent = (this.dataset.nudgeMax || '').replace('[percent]', this.percentText(top?.percent ?? percent));
      }

      this.querySelector('[data-subtotal]').textContent = this.money(subtotal);
      const discountRow = this.querySelector('[data-discount-row]');
      discountRow.hidden = discount <= 0;
      const label = this.querySelector('[data-discount-label]');
      label.dataset.template ??= label.textContent;
      label.textContent = mixed ? label.dataset.labelMixed : label.dataset.template.replace('[percent]', this.percentText(percent));
      this.querySelector('[data-discount]').textContent = `-${this.money(discount)}`;
      this.querySelector('[data-total]').textContent = this.money(subtotal - discount);
      if (this.addButton) this.addButton.disabled = units === 0 || this.busy;
      this.selection = selected;
    }

    renderLines(selected) {
      const list = this.querySelector('[data-lines]');
      const empty = this.querySelector('[data-lines-empty]');
      list.innerHTML = '';
      empty.hidden = selected.length > 0;
      selected.forEach(({ row, quantity }) => {
        const price = Number(row.dataset.price);
        const item = document.createElement('li');
        item.className = 'brand-builder__line';
        if (row.dataset.image) {
          const image = document.createElement('img');
          image.src = row.dataset.image; image.alt = ''; image.width = 48; image.height = 48; image.loading = 'lazy';
          item.append(image);
        }
        const copy = document.createElement('p');
        const title = document.createElement('strong'); title.textContent = row.dataset.title; title.translate = false;
        const each = document.createElement('span'); each.textContent = `${quantity} × ${this.money(price)}`;
        copy.append(title, each);
        const total = document.createElement('span');
        total.className = 'brand-builder__line-total';
        total.textContent = this.money(price * quantity);
        item.append(copy, total);
        list.append(item);
      });
    }

    async addToCart() {
      if (!window.RosviaCart?.addMany || !this.selection?.length || this.busy) return;
      const error = this.querySelector('[data-error-line]');
      error.hidden = true;
      this.busy = true;
      this.addButton.disabled = true;
      this.addButton.setAttribute('aria-busy', 'true');
      const units = this.selection.reduce((sum, line) => sum + line.quantity, 0);
      const first = this.selection[0].row;
      try {
        await window.RosviaCart.addMany(
          this.selection.map(({ row, quantity }) => ({ id: Number(row.dataset.variantId), quantity })),
          { item: { product_title: this.dataset.brand, image: first.dataset.image }, quantity: units }
        );
        this.rows.forEach(row => { const input = row.querySelector('[data-row-quantity]'); if (input && !input.disabled) input.value = 0; });
      } catch (failure) {
        error.textContent = failure.message || this.dataset.error;
        error.hidden = false;
      } finally {
        this.busy = false;
        this.addButton.removeAttribute('aria-busy');
        this.update();
      }
    }
  });
}
